/**
 * ⟦A-20⟧ **La surcharge d'énergie** — une jauge qui a le droit de dépasser son maximum,
 * temporairement.
 *
 * *« Deux dodos bien placés, ça peut faire une sorte de petit bonus d'énergie. La fatigue,
 * c'est peut-être une jauge qui a le droit d'être au-dessus de son chiffre de départ, de façon
 * temporaire. Deux dodos, tu as plus d'énergie que d'habitude — à un moment précis, pas deux
 * dodos tout le temps égale ça. »* (Mathieu)
 *
 * ── Ce que ça répare ──────────────────────────────────────────────────────────
 *
 * Les **boissons énergisantes** de la boutique ⟦A-11 §4⟧ n'avaient aucun sens sous un plafond
 * dur à 100 : boire à 90 gagnait 10 et jetait le reste. Une jauge qui peut dépasser leur donne
 * enfin une fonction — et la **grasse matinée** est la même mécanique, obtenue par le repos au
 * lieu de l'achat.
 *
 * ── Pourquoi ça s'auto-limite, et pourquoi il n'y a AUCUNE règle anti-abus ────
 *
 * **L'énergie dit combien d'actions on peut faire ; les heures disent combien la journée en
 * contient.** Une surcharge permet donc d'en faire plus — ce qui pousse au-delà des
 * `H_EVEILLE_PAR_JOUR`, là où la fatigue attend ⟦A-18, A-19⟧. Le bonus se paie de lui-même.
 * C'est le principe d'⟦A-11 §2⟧ (le retour chez les parents s'équilibre par le plafond de
 * compétence, pas par une interdiction).
 *
 * Et **spammer `sleep` reste contre-productif** sans qu'on l'interdise : depuis ⟦A-19⟧ la nuit
 * coûte 8 h, donc chaque nuit déclenche les cinq pressions d'⟦A-15⟧ — idées qui périment,
 * tendances qui meurent, rival qui monte, opportunités qui expirent, érosion du silence.
 *
 * ── L'état neutre par défaut (CLAUDE.md, ⟦A-10⟧) ────────────────────────────────
 *
 * `state.energy_surcharge` est **optionnel** : `undefined` ⇔ `0` ⇔ plafond 100 ⇔ le
 * comportement d'avant l'avenant, au point près. Un joueur qui ne dort jamais deux fois de
 * suite et n'achète aucune boisson ne voit jamais cette mécanique, et **aucune ancre ne bouge**
 * (elles n'appellent que `computeVideoOutcome`, qui ignore l'énergie). Aucun état sérialisé
 * n'est à migrer.
 *
 * Pureté (CLAUDE.md) : aucune I/O, aucune horloge système, aucun `Math.random`.
 */
// ─────────────────────────────────────────────────────────────────────────────
// Constantes normatives — les trois ✎ que l'avenant laisse ouverts
// ─────────────────────────────────────────────────────────────────────────────
/** Plafond ordinaire de l'énergie (SPEC-MOTEUR §5). C'est lui que la surcharge dépasse. */
export const ENERGIE_MAX = 100;
/**
 * ✎ **Surcharge maximale au-dessus de `ENERGIE_MAX`.** Choisie pour valoir « une action de
 * plus, jamais deux » : une longue coûte 45 ⚡, un short 12 ⚡. À 30, une grasse matinée +
 * une boisson achètent une longue de rab, pas une journée entière — donc le joueur y gagne un
 * arbitrage, pas un régime.
 */
export const SURCHARGE_MAX = 30; // ✎
/**
 * ✎ **Ce que rend la grasse matinée** — la 2e nuit consécutive, et elle seule.
 *
 * A-20 le dit en toutes lettres : *« pas deux dodos tout le temps égale ça »*. La 3e nuit et
 * les suivantes ne rendent donc **rien de plus** : c'est ce qui distingue « bien placer ses
 * dodos » de « dormir en boucle », sans qu'aucune règle n'ait à interdire la seconde.
 */
export const SURCHARGE_GRASSE_MATINEE = 20; // ✎
/**
 * ✎ **Retour à la normale** : points de surcharge perdus par jour simulé franchi.
 *
 * C'est le « temporaire » de l'avenant, et il ne peut pas venir de la dépense seule : une
 * surcharge non consommée resterait acquise indéfiniment, et la grasse matinée deviendrait un
 * plafond permanent au bout de deux nuits. À 10/jour, une surcharge pleine s'éteint en trois
 * jours si l'on ne s'en sert pas — assez pour la placer, trop court pour la thésauriser.
 */
export const SURCHARGE_DECAY_PAR_JOUR = 10; // ✎
/** Rang de la nuit consécutive qui donne la grasse matinée ✎ — la **deuxième**. */
export const NUIT_GRASSE_MATINEE = 2;
// ─────────────────────────────────────────────────────────────────────────────
// Lectures — pures, utilisables par l'UI pour dessiner une jauge au-dessus de son max
// ─────────────────────────────────────────────────────────────────────────────
/** Surcharge actuellement accordée, en points au-dessus de 100. `undefined` ⇔ 0. */
export function surchargeAccordee(state) {
    return Math.max(0, state.energy_surcharge ?? 0);
}
/**
 * Plafond d'énergie **effectif** — c'est la borne que `appliquerClamps` applique, et c'est
 * elle que l'UI doit dessiner : une jauge au-dessus de son maximum doit **se voir** comme
 * telle, pas être écrêtée à l'écran.
 */
export function plafondEnergie(state) {
    return ENERGIE_MAX + surchargeAccordee(state);
}
/** Le joueur est-il en surcharge **en ce moment** (énergie effectivement au-dessus de 100) ? */
export function enSurcharge(state) {
    return state.energy > ENERGIE_MAX;
}
// ─────────────────────────────────────────────────────────────────────────────
// Écritures — toutes MUTENT le clone de travail de la file §2.3
// ─────────────────────────────────────────────────────────────────────────────
/**
 * Accorde `points` de surcharge, borné par `SURCHARGE_MAX`. Rend ce qui a **réellement** été
 * accordé (0 si la surcharge était déjà pleine) — l'appelant s'en sert pour ne rien annoncer
 * au joueur quand rien n'a bougé.
 *
 * ⚠️ Accorder de la surcharge **ne remplit pas** la jauge : ça ouvre le plafond. C'est le
 * `gauge_delta` de la boisson ou le `+80` de la nuit qui remplit — et la distinction compte,
 * sinon boire à 20 ⚡ téléporterait à 120.
 */
export function accorderSurcharge(state, points) {
    if (points <= 0)
        return 0;
    const avant = surchargeAccordee(state);
    const apres = Math.min(SURCHARGE_MAX, avant + points);
    state.energy_surcharge = apres;
    return apres - avant;
}
/**
 * ⟦A-20⟧ **La grasse matinée.** À appeler à chaque `sleep`, après avoir incrémenté le compteur
 * de nuits consécutives. Rend la surcharge accordée (0 pour la 1re nuit, 0 dès la 3e).
 *
 * Le compteur vit dans `Counters.consecutive_sleeps` et **toute action qui n'est pas un
 * `sleep` le remet à zéro** : c'est cette remise à zéro qui donne son sens à « consécutifs »,
 * et c'est `applyAction` qui la porte, pour qu'aucune action ne puisse l'oublier.
 */
export function grasseMatinee(state) {
    const nuits = state.counters.consecutive_sleeps ?? 0;
    if (nuits !== NUIT_GRASSE_MATINEE)
        return 0;
    return accorderSurcharge(state, SURCHARGE_GRASSE_MATINEE);
}
/**
 * Décroissance d'un jour simulé — à appeler à **chaque frontière de jour** franchie (§2.3
 * pas 5). Rend les points perdus (0 s'il n'y avait pas de surcharge : l'état neutre ne coûte
 * pas une ligne de récapitulatif).
 *
 * ⚠️ Ne touche PAS `state.energy` : le clamp du pas 9 s'en charge, et lui seul. Deux endroits
 * qui écrêtent la même jauge, c'est un solde inexplicable à l'écran — exactement ce qu'⟦A-16⟧
 * interdit.
 */
export function decroitreSurcharge(state) {
    const avant = surchargeAccordee(state);
    if (avant <= 0)
        return 0;
    const apres = Math.max(0, avant - SURCHARGE_DECAY_PAR_JOUR);
    state.energy_surcharge = apres;
    return avant - apres;
}
/**
 * Surcharge qu'un **consommable** de boutique ouvre : la somme de ce qu'il rend en énergie.
 *
 * Deux formes, parce que la boutique en a deux (`boutique.ts`) : le `gauge_delta` d'une
 * boisson `immediat`, et le `sleep_recovery_add` d'un consommable `prochaine_nuit`.
 *
 * ⚠️ **Les permanents en sont exclus, et c'est le cœur de la règle.** Un bon lit fait
 * récupérer mieux (`sleep_recovery_add` relu depuis `inventaire.permanents`) ; il ne rend pas
 * surhumain. Seul ce qui se **consomme** ouvre le plafond — d'où l'appel depuis les deux
 * chemins consommables de `boutique.ts`, et depuis eux seuls.
 */
export function surchargeDunConsommable(effets) {
    let total = 0;
    for (const e of effets) {
        if (e.type === "gauge_delta" && e.gauge === "energie" && e.value > 0)
            total += e.value;
        if (e.type === "sleep_recovery_add" && e.gauge === "energie" && e.value > 0)
            total += e.value;
    }
    return total;
}
//# sourceMappingURL=surcharge.js.map